/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { EMPTY, switchMap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { acdStatusChange, requestedAcdStatusChange } from '../+state/user.actions';
import { selectComputedAgentStatus } from '../../call/+state/call.selectors';
import { AgentStatus, AgentStatusRecord } from 'CalltakingCoreApi';
import { filter } from 'rxjs/operators';
import { selectIsDeviceConnectedAndHeadsetDisconnected } from '../../call/+state/cchub.selectors';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { Topic } from '../model/multiple-subscriptions';
import { selectIsPrimaryConnectionActive } from '../../call/+state/media.selectors';
import { RequestedAgentStatus } from '../model/requested-agent-status';

@Injectable({
    providedIn: 'root'
})
export class AcdStatusService extends StompBaseConsumer {
    private readonly API_BASE_URL = '/api/acd/v1/';

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
            let active$ = this.store.select(selectIsPrimaryConnectionActive);
            active$
                .pipe(switchMap((active) => (active ? this.store.select(selectComputedAgentStatus).pipe(filter((val) => !!val)) : EMPTY)))
                .subscribe((requestedAgentStatus) => this.store.dispatch(acdStatusChange({requestedAgentStatus: requestedAgentStatus})));
            active$
                .pipe(switchMap((active) => (active ? this.store.select(selectIsDeviceConnectedAndHeadsetDisconnected).pipe(filter((val) => !!val)) : EMPTY)))
                .pipe(filter((val) => !!val))
                .subscribe(() => this.store.dispatch(requestedAcdStatusChange({ agentStatus: 'NOT_READY' })));
    }

    protected getTopics(): Topic[] {
        return [Topic.REQUEST_USER_STATUS];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any, clusterName: string) => void } {
        return {
            [Topic.REQUEST_USER_STATUS]: this.handleRequestUserStatusMessage.bind(this)
        };
    }

    protected handleRequestUserStatusMessage(agentStatus: AgentStatus, clusterName: string) {
        this.handleRequestStatusUpdate(agentStatus, clusterName);
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription success: ${receiptId}`);
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    private handleRequestStatusUpdate(agentStatus: AgentStatus, clusterName: string) {
        this.store.dispatch(acdStatusChange({ requestedAgentStatus: { status: agentStatus, clusterName: clusterName, isAgentRequested: false } }));
    }

    public reportAgentStatus(requestedAgentStatus: RequestedAgentStatus) {
        console.log(`Reporting agent status ${requestedAgentStatus.status} for cluster ${requestedAgentStatus.clusterName} with reason ${requestedAgentStatus.statusReasonDisplayName} and reasonId ${requestedAgentStatus.statusReasonId}`);
        let params = new HttpParams();
        params = requestedAgentStatus.isAgentRequested ? params.append('agentRequested', requestedAgentStatus.isAgentRequested) : params;
        params = requestedAgentStatus.statusReasonDisplayName ? params.append('statusReasonDisplayName', requestedAgentStatus.statusReasonDisplayName) : params;
        params = requestedAgentStatus.statusReasonId ? params.append('statusReasonId', requestedAgentStatus.statusReasonId) : params;
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(requestedAgentStatus.clusterName, `${this.API_BASE_URL}report-status/${requestedAgentStatus.status}`);
        return this.http.post<AgentStatusRecord>(url, {}, { params: params });
    }
}
